import { CommonModule } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { FilteringChipsComponent } from '../../../../../shared/components/filtering-chips/filtering-chips.component';
import { TablePaginationComponent } from '../../../../../shared/components/table-pagination/table-pagination.component';
import { OrdersApiService } from '../../../services/orders-api.service';
import { GetOrdersResponse } from '../../../models/get-orders-response';
import { LimitOffsetPagination } from '../../../../../shared/models/limit-offset-pagination';
import { ToastNotificationService } from '../../../../../notifications/services/toast-notification.service';
import { ToastNotificationType } from '../../../../../notifications/models/toast-notification-type';
import { debounceTime, distinctUntilChanged, Subject } from 'rxjs';
import { ExportExcelButtonComponent } from '../../../../../shared/components/export-excel-button/export-excel-button.component';
import { FiltersDropdownComponent, FilterDef, FilterValues } from '../../../../../shared/filters-dropdown';
import { DriversManagementApiService } from '../../../../drivers-management/services/drivers-management-api.service';
import { Driver } from '../../../../drivers-management/models/driver';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { LanguageService } from '../../../../../shared/services/translations/language.service';

@Component({
  selector: 'app-delivery-orders',
  imports: [CommonModule, RouterLink, FormsModule, TablePaginationComponent, FilteringChipsComponent, FiltersDropdownComponent, ExportExcelButtonComponent, TranslateModule],
  templateUrl: './delivery-orders.component.html',
  styleUrl: './delivery-orders.component.css'
})
export class DeliveryOrdersComponent implements OnInit {

  apiService = inject(OrdersApiService);
  driversApiService = inject(DriversManagementApiService);
  ordersResponse: GetOrdersResponse | undefined;
  pagination: LimitOffsetPagination = { limit: 15, offset: 0 };
  toastNotificationService = inject(ToastNotificationService);
  translate = inject(TranslateService);
  languageService = inject(LanguageService);


  searchTerm = '';
  selectedStatus = '';
  filterValues: FilterValues = {};
  orderFilters: FilterDef[] = [];
  drivers: Driver[] = [];

  statusFilters: { name: string, value: string }[] = [
    { name: 'orders.status_filters.all', value: '' },
    { name: 'orders.status_filters.placed', value: 'placed' },
    { name: 'orders.status_filters.preparing_order', value: 'preparing_order' },
    { name: 'orders.status_filters.ready_for_pickup', value: 'ready_for_pickup' },
    { name: 'orders.status_filters.out_for_delivery', value: 'out_for_delivery' },
    { name: 'orders.status_filters.delivered', value: 'delivered' },
    { name: 'orders.status_filters.cancelled', value: 'cancelled' },
    { name: 'orders.status_filters.refunded', value: 'refunded' }
  ];

  search = '';
  searchSubject = new Subject<string>(); // for debounce

  ngOnInit(): void {
    this.orderFilters = this.buildFilters();
    this.getOrders();
    this.loadDrivers();

    // setup debounced search
    this.searchSubject.pipe(
      debounceTime(500),
      distinctUntilChanged(),
    ).subscribe(searchText => {
      this.search = searchText;
      this.pagination = { limit: this.pagination.limit, offset: 0 };
      this.getOrders();
    });

    this.translate.onLangChange.subscribe(() => {
      this.orderFilters = this.buildFilters();
      this.getOrders();
    });
  }

  private loadDrivers(): void {
    this.driversApiService.getDrivers({ limit: 50, offset: 0 }).subscribe({
      next: (res) => {
        this.drivers = res.results;
        this.orderFilters = this.buildFilters();
      },
      error: () => { /* driver filter just stays empty on failure */ }
    });
  }

  private buildFilters(): FilterDef[] {
    const currency = this.translate.instant('common.currency');
    return [
      {
        kind: 'numberRange',
        key: 'price',
        label: `${this.translate.instant('orders.filters.total')} (${currency})`,
        minPlaceholder: this.translate.instant('orders.filters.from'),
        maxPlaceholder: this.translate.instant('orders.filters.to'),
      },
      {
        kind: 'dateRange',
        key: 'placedAt',
        label: this.translate.instant('orders.filters.placed_at'),
        placeholder: this.translate.instant('orders.filters.date_placeholder'),
      },
      {
        kind: 'select',
        key: 'driver',
        label: this.translate.instant('orders.filters.driver'),
        placeholder: this.translate.instant('orders.filters.all_drivers'),
        options: [
          { label: this.translate.instant('orders.unassigned_driver'), value: 'none' },
          ...this.drivers.map(d => ({ label: d.name, value: d.id })),
        ],
      },
    ];
  }

  getOrders(status?: string) {
    if (status !== undefined) this.selectedStatus = status;
    this.ordersResponse = undefined;

    this.apiService.getOrders(this.pagination, this.selectedStatus, this.search, this.toApiFilters()).subscribe({
      next: orders => {
        this.ordersResponse = orders;
      },
      error: error => {
        this.toastNotificationService.createNotification(this.translate.instant('orders.errors.fetch_failed'), ToastNotificationType.ERROR);
      }
    });
  }

  onFiltersChange(values: FilterValues) {
    this.filterValues = values;
    this.pagination = { limit: this.pagination.limit, offset: 0 };
    this.getOrders();
  }

  private toApiFilters(): Record<string, string | number> {
    const out: Record<string, string | number> = {};
    const price = this.filterValues['price'];
    if (price?.min != null) out['min_total'] = price.min;
    if (price?.max != null) out['max_total'] = price.max;
    const range = this.filterValues['placedAt'];
    if (range?.start) out['placed_from'] = range.start;
    if (range?.end) out['placed_to'] = range.end;
    const driver = this.filterValues['driver'];
    if (driver != null && driver !== '') out['driver'] = driver;
    return out;
  }

  updatePagination(pagination: LimitOffsetPagination) {
    this.pagination = pagination;
    this.getOrders();
  }

  onSearchChange(event: any) {
    const value = event.target.value.trim().replace(/^#/, '');
    this.searchSubject.next(value);
  }

}
